import { ConnectionStatus, wsManager, MarketType } from "./ws-manager";

export type { MarketType };

export interface TradeData {
  id: string | number;
  price: number;
  amount: number;
  timestamp: number;
  side: "buy" | "sell";
  symbol: string;
  cost?: number;
  datetime?: string;
  info?: any;
}

export interface OrderbookData {
  bids: Array<[number, number]>;
  asks: Array<[number, number]>;
  timestamp: number;
  symbol: string;
  nonce?: number;
  datetime?: string;
}

export interface TickerData {
  symbol: string;
  timestamp: number;
  datetime: string;
  high: number;
  low: number;
  bid: number;
  bidVolume: number;
  ask: number;
  askVolume: number;
  vwap: number;
  open: number;
  close: number;
  last: number;
  previousClose: number;
  change: number;
  percentage: number;
  average: number;
  baseVolume: number;
  quoteVolume: number;
  info?: any;
}

export interface OHLCVData {
  stream: string;
  data: Array<[number, number, number, number, number, number]>;
}

export interface MarketDataSubscription {
  symbol: string;
  type: "orderbook" | "trades" | "ticker" | "ohlcv";
  marketType: MarketType;
  limit?: number;
  interval?: string;
}

export class MarketDataWebSocketService {
  private static instance: MarketDataWebSocketService;
  private isInitialized = false;
  private activeSubscriptions: Map<string, MarketDataSubscription> = new Map();
  private callbacks: Map<string, Set<(data: any) => void>> = new Map();
  private connectedMarketTypes: Set<MarketType> = new Set();
  private subscriptionSent: Map<string, boolean> = new Map();
  private pendingSubscriptions: Map<MarketType, Set<string>> = new Map();
  private connectionStatusMap: Map<MarketType, ConnectionStatus> = new Map();
  private activeStreamSubscriptions: Map<string, Set<string>> = new Map();
  private lastDataCache: Map<string, any> = new Map();
  private wsConnections: Map<MarketType, string> = new Map();

  constructor() {
    if (typeof window !== "undefined") {
      const baseWsUrl =
        import.meta.env.VITE_WEBSOCKET_URL ||
        `${window.location.protocol === "https:" ? "wss:" : "ws:"}//${window.location.host}`;
      this.wsConnections.set("spot", `${baseWsUrl}/api/exchange/market`);
      this.wsConnections.set("eco", `${baseWsUrl}/api/ecosystem/market`);
      this.wsConnections.set("futures", `${baseWsUrl}/api/futures/market`);
    } else {
      this.wsConnections.set("spot", "ws://localhost:18790/api/exchange/market");
      this.wsConnections.set("eco", "ws://localhost:18790/api/ecosystem/market");
      this.wsConnections.set("futures", "ws://localhost:18790/api/futures/market");
    }

    this.wsConnections.forEach((_, marketType) => {
      this.connectionStatusMap.set(marketType, ConnectionStatus.DISCONNECTED);
      this.pendingSubscriptions.set(marketType, new Set());
      this.activeStreamSubscriptions.set(marketType, new Set());
    });
  }

  public static getInstance(): MarketDataWebSocketService {
    if (!MarketDataWebSocketService.instance) {
      MarketDataWebSocketService.instance = new MarketDataWebSocketService();
    }
    return MarketDataWebSocketService.instance;
  }

  public getProviderLimit(tickSize: number): number {
    const provider = typeof window !== "undefined" ? import.meta.env.NEXT_PUBLIC_EXCHANGE : "bin";
    if (provider === "xt") {
      if (tickSize <= 0.01) return 5;
      if (tickSize <= 0.1) return 10;
      if (tickSize <= 1) return 20;
      return 50;
    } else if (provider === "kuc") {
      if (tickSize <= 0.01) return 50;
      if (tickSize <= 0.1) return 50;
      if (tickSize <= 1) return 100;
      return 100;
    } else {
      if (tickSize <= 0.01) return 40;
      if (tickSize <= 0.1) return 80;
      if (tickSize <= 1) return 160;
      return 320;
    }
  }

  public initialize(): void {
    if (this.isInitialized) return;
    this.isInitialized = true;
  }

  private formatSymbol(symbol: string): string {
    if (symbol.includes("/")) return symbol;
    if (symbol.includes("-")) return symbol.replace("-", "/");
    if (symbol.includes("_")) return symbol.replace("_", "/");

    const midPoint = Math.floor(symbol.length / 2);
    for (let i = Math.max(2, midPoint - 2); i <= Math.min(symbol.length - 2, midPoint + 2); i++) {
      const base = symbol.substring(0, i);
      const quote = symbol.substring(i);
      if (quote.length >= 3 && quote.length <= 4) {
        return `${base}/${quote}`;
      }
    }

    const currency = symbol.substring(0, midPoint);
    const pair = symbol.substring(midPoint);
    return `${currency}/${pair}`;
  }

  private ensureConnection(marketType: MarketType): void {
    if (this.connectedMarketTypes.has(marketType)) return;

    const url = this.wsConnections.get(marketType);
    if (!url) {
      console.error(`No WebSocket URL defined for market type: ${marketType}`);
      return;
    }

    wsManager.connect(url, marketType);
    this.connectedMarketTypes.add(marketType);

    wsManager.addStatusListener((status) => {
      this.connectionStatusMap.set(marketType, status);
      if (status === ConnectionStatus.CONNECTED) {
        this.processPendingSubscriptions(marketType);
      }
    }, marketType);
  }

  private processPendingSubscriptions(marketType: MarketType): void {
    if (!this.pendingSubscriptions.has(marketType)) return;

    const pendingKeys = this.pendingSubscriptions.get(marketType)!;
    if (pendingKeys.size === 0) return;

    const uniqueSubscriptions = new Map<string, MarketDataSubscription>();

    for (const key of pendingKeys) {
      const subscription = this.activeSubscriptions.get(key);
      if (subscription) {
        const dedupeKey = `${subscription.type}:${subscription.symbol}`;
        uniqueSubscriptions.set(dedupeKey, subscription);
      }
    }

    for (const subscription of uniqueSubscriptions.values()) {
      this.sendSubscriptionMessage(subscription);
    }

    this.pendingSubscriptions.get(marketType)!.clear();
  }

  private getSubscriptionKey(
    type: string,
    symbol: string,
    marketType: MarketType,
    interval?: string
  ): string {
    return interval
      ? `${marketType}:${type}:${symbol}:${interval}`
      : `${marketType}:${type}:${symbol}`;
  }

  private getStreamKey(
    type: string,
    limit?: number,
    interval?: string
  ): string {
    if (type === "ohlcv" && interval) {
      return `${type}:${interval}`;
    }
    return limit ? `${type}:${limit}` : type;
  }

  private isStreamSubscribed(
    streamKey: string,
    marketType: MarketType
  ): boolean {
    return (
      this.activeStreamSubscriptions.get(marketType)?.has(streamKey) || false
    );
  }

  public subscribe<T>(
    subscription: MarketDataSubscription,
    callback: (data: T) => void
  ): () => void {
    if (!this.isInitialized) {
      this.initialize();
    }

    const formattedSymbol = this.formatSymbol(subscription.symbol);
    const { type, marketType, interval } = subscription;

    let limit = subscription.limit;
    if (type === "orderbook" && !limit) {
      const tickSize = 0.01;
      limit = this.getProviderLimit(tickSize);
    }

    const formattedSubscription = {
      ...subscription,
      symbol: formattedSymbol,
      limit,
    };

    const subscriptionKey = this.getSubscriptionKey(
      type,
      formattedSymbol,
      marketType,
      interval
    );
    const streamKey = this.getStreamKey(type, limit, interval);

    this.ensureConnection(marketType);

    if (!this.callbacks.has(subscriptionKey)) {
      this.callbacks.set(subscriptionKey, new Set());
    }
    this.callbacks.get(subscriptionKey)!.add(callback);

    if (this.lastDataCache.has(subscriptionKey)) {
      const cachedData = this.lastDataCache.get(subscriptionKey);
      try {
        callback(cachedData);
      } catch (error) {
        console.error(`Error in immediate callback for ${subscriptionKey}:`, error);
      }
    }

    this.activeSubscriptions.set(subscriptionKey, formattedSubscription);

    const isConnected =
      this.connectionStatusMap.get(marketType) === ConnectionStatus.CONNECTED;

    if (isConnected) {
      if (
        !this.subscriptionSent.has(subscriptionKey) &&
        !this.isStreamSubscribed(streamKey, marketType)
      ) {
        this.sendSubscriptionMessage(formattedSubscription);
        this.subscriptionSent.set(subscriptionKey, true);
        this.activeStreamSubscriptions.get(marketType)!.add(streamKey);
      }
    } else {
      if (!this.pendingSubscriptions.has(marketType)) {
        this.pendingSubscriptions.set(marketType, new Set());
      }
      this.pendingSubscriptions.get(marketType)!.add(subscriptionKey);
    }

    wsManager.subscribe(
      streamKey,
      (data) => {
        try {
          if (type === "ohlcv") {
            const normalizedData = this.normalizeOHLCVData(data, interval || "1h");
            if (!normalizedData) {
              console.error("[Market Data WS] Failed to normalize data:", data);
              return;
            }

            if (interval && normalizedData.stream) {
              const streamParts = normalizedData.stream.split(":");
              const dataInterval = streamParts[1];
              if (dataInterval !== interval) return;
            }

            const callbacks = this.callbacks.get(subscriptionKey);
            if (callbacks && callbacks.size > 0) {
              callbacks.forEach((cb) => {
                try {
                  cb(normalizedData as any);
                } catch (error) {
                  console.error(
                    `[Market Data WS] Error in callback for ${subscriptionKey}:`,
                    error
                  );
                }
              });
            }
            return;
          }

          let actualData = data;
          const dataSymbol = data.symbol;
          if (data.data && typeof data.data === 'object') {
            actualData = data.data;
          }

          if (type === "trades") {
            const checkSymbol = dataSymbol || actualData.symbol || (Array.isArray(actualData) && actualData.length > 0 && actualData[0].symbol);
            if (checkSymbol && checkSymbol !== formattedSymbol) return;
          }

          if ((type === "orderbook" || type === "ticker") && !actualData.symbol) {
            actualData.symbol = formattedSymbol;
            actualData.timestamp = actualData.timestamp || Date.now();
          }

          const callbacks = this.callbacks.get(subscriptionKey);
          if (callbacks) {
            this.lastDataCache.set(subscriptionKey, actualData);
            callbacks.forEach((cb) => {
              try {
                cb(actualData);
              } catch (error) {
                console.error(`Error in callback for ${subscriptionKey}:`, error);
              }
            });
          }
        } catch (error) {
          console.error(`Error processing WebSocket data for ${subscriptionKey}:`, error);
        }
      },
      marketType
    );

    return () => {
      this.unsubscribe(formattedSubscription, callback);
    };
  }

  private normalizeOHLCVData(data: any, interval: string): any {
    if (
      Array.isArray(data) &&
      data.length > 0 &&
      Array.isArray(data[0]) &&
      data[0].length >= 6
    ) {
      return { stream: `ohlcv:${interval}`, data: data };
    }

    if (data && data.stream && data.stream.startsWith("ohlcv:") && Array.isArray(data.data)) {
      return data;
    }

    if (typeof data === "string") {
      try {
        const parsedData = JSON.parse(data);
        if (parsedData && parsedData.stream && parsedData.stream.startsWith("ohlcv:") && Array.isArray(parsedData.data)) {
          return parsedData;
        }
        if (Array.isArray(parsedData) && parsedData.length >= 6) {
          return { stream: `ohlcv:${interval}`, data: [parsedData] };
        }
      } catch (e) {
        console.error("[Market Data WS] Failed to parse string data:", e);
      }
    }

    if (data && data.stream && data.data) return data;
    return null;
  }

  public unsubscribe<T>(
    subscription: MarketDataSubscription,
    callback: (data: T) => void
  ): void {
    const { type, symbol, marketType, interval } = subscription;
    const formattedSymbol = this.formatSymbol(symbol);
    const subscriptionKey = this.getSubscriptionKey(
      type,
      formattedSymbol,
      marketType,
      interval
    );
    const streamKey = this.getStreamKey(type, subscription.limit, interval);

    const callbacks = this.callbacks.get(subscriptionKey);
    if (callbacks) {
      callbacks.delete(callback);

      if (callbacks.size === 0) {
        let shouldUnsubscribe = true;
        for (const [key, sub] of this.activeSubscriptions.entries()) {
          if (
            key !== subscriptionKey &&
            sub.marketType === marketType &&
            sub.type === type &&
            this.getStreamKey(sub.type, sub.limit, sub.interval) === streamKey
          ) {
            shouldUnsubscribe = false;
            break;
          }
        }

        this.callbacks.delete(subscriptionKey);
        this.activeSubscriptions.delete(subscriptionKey);

        if (shouldUnsubscribe) {
          this.activeStreamSubscriptions.get(marketType)?.delete(streamKey);
        }

        if (this.subscriptionSent.has(subscriptionKey) && shouldUnsubscribe) {
          this.sendUnsubscriptionMessage(subscription);
          this.subscriptionSent.delete(subscriptionKey);
        }

        if (this.pendingSubscriptions.has(marketType)) {
          this.pendingSubscriptions.get(marketType)!.delete(subscriptionKey);
        }
      }
    }
  }

  private sendSubscriptionMessage(subscription: MarketDataSubscription): void {
    const { symbol, type, marketType, limit, interval } = subscription;
    const formattedSymbol = this.formatSymbol(symbol);
    const streamKey = this.getStreamKey(type, limit, interval);

    if (this.isStreamSubscribed(streamKey, marketType)) return;

    const message = {
      action: "SUBSCRIBE",
      payload: {
        type,
        ...(limit ? { limit } : {}),
        ...(interval ? { interval } : {}),
        symbol: formattedSymbol,
      },
    };

    wsManager.sendMessage(message, marketType);
    this.activeStreamSubscriptions.get(marketType)!.add(streamKey);
    this.subscriptionSent.set(
      this.getSubscriptionKey(type, formattedSymbol, marketType, interval),
      true
    );
  }

  private sendUnsubscriptionMessage(subscription: MarketDataSubscription): void {
    const { symbol, type, marketType, limit, interval } = subscription;
    const formattedSymbol = this.formatSymbol(symbol);
    const streamKey = this.getStreamKey(type, limit, interval);

    const message = {
      action: "UNSUBSCRIBE",
      payload: {
        type,
        ...(limit ? { limit } : {}),
        ...(interval ? { interval } : {}),
        symbol: formattedSymbol,
      },
    };

    wsManager.sendMessage(message, marketType);
    this.activeStreamSubscriptions.get(marketType)?.delete(streamKey);
  }

  public subscribeToConnectionStatus(
    callback: (status: ConnectionStatus) => void,
    marketType: MarketType
  ): () => void {
    wsManager.addStatusListener(callback, marketType);
    return () => wsManager.removeStatusListener(callback, marketType);
  }

  public getConnectionStatus(marketType: MarketType): ConnectionStatus {
    return (
      this.connectionStatusMap.get(marketType) || ConnectionStatus.DISCONNECTED
    );
  }

  public cleanup(): void {
    this.activeSubscriptions.forEach((subscription) => {
      if (
        this.subscriptionSent.has(
          this.getSubscriptionKey(
            subscription.type,
            subscription.symbol,
            subscription.marketType,
            subscription.interval
          )
        )
      ) {
        this.sendUnsubscriptionMessage(subscription);
      }
    });

    this.connectedMarketTypes.forEach((marketType) => {
      wsManager.close(marketType);
      this.connectionStatusMap.set(marketType, ConnectionStatus.DISCONNECTED);
    });

    this.activeSubscriptions.clear();
    this.callbacks.clear();
    this.connectedMarketTypes.clear();
    this.subscriptionSent.clear();
    this.activeStreamSubscriptions.forEach((set) => set.clear());
    this.pendingSubscriptions.forEach((set) => set.clear());
    this.isInitialized = false;
  }
}

export const marketDataWs = MarketDataWebSocketService.getInstance();
